import { MutationTree } from 'vuex';
import { FormState } from '@/store/modules/form/index';
import { FormHeaders } from '@/models/Form.model';
import { City } from '@/models/form/PersonalDetails.model';
import { Form101DataModel } from '@/models/form/Form101Data.model';
import { Document, Documents } from '@/models/form/Documents.model';
import {
  ADD_EMPLOYEE_CHILD, ADD_EXTERNAL_INCOME,
  FORM_RESET_STATE,
  REMOVE_EMPLOYEE_CHILD,
  RESET_FORM_ERRORS,
  SET_CITIES,
  SET_CREATE_FORM_ERRORS, SET_CREATE_FORM_LOADING,
  SET_EDIT_FORM_ERRORS,
  SET_FORM101_DATA,
  SET_FORM101_ID,
  SET_FORM101_META_DATA,
  SET_FORM_ERRORS,
  SET_FORM_ERRORS_OPEN,
  SET_FORM_ERRORS_SHOW,
  SET_FORM_HEADERS,
  SET_FORM_LOADING_MODE,
  SET_FORM_READ_ONLY_MODE,
  SET_FORM_REJECTED_REASON,
  SET_FORM_SUCCESS_DIALOG,
  SET_NO_PDF_ERROR,
  SET_NO_PDF_ERRORS,
  SET_OPEN_APPROVE_FORM_DIALOG,
  SET_OPEN_CHOOSE_FORM_OFFICE_MODAL,
  SET_OPEN_CONFIRM_FORM_DIALOG,
  SET_OPEN_FORM_101_CHANGES_DIALOG,
  SET_OPEN_FORM_STATUS_HISTORY_DIALOG,
  SET_OPEN_REJECT_FORM_DIALOG,
  SET_OPEN_SAVE_BEFORE_LEAVE_WIZARD_MODAL,
  SET_SAVE_BEFORE_LEAVE_WIZARD_ROUTE_NAME,
  SET_SETTLEMENTS,
  SET_SUBMIT_FORM_ERRORS,
  SET_TOOLTIP_ID,
  UPDATE_EMPLOYEE_CHILD_DETAILS,
  REMOVE_EXTERNAL_INCOME,
  UPDATE_EXTERNAL_INCOME,
  SET_OPEN_DELETE_FORM_DIALOG,
  SET_APPROVE_DETAILS_CHANGES,
  SET_SKIP_SAVE_BEFORE_LEAVE_WIZARD_MODAL,
  SET_DELETE_FORM_ERRORS,
  SET_OPEN_CHANGES_NOTIFICATION_MODAL,
} from '@/types/form.types';
import { ChildrenDetails } from '@/models/form/ChildrenDetails.model';
import { WizardFormError } from '@/models/form/WizardFormError';
import { initialState } from '@/store/modules/form';
import { Form101MetaData } from '@/models/form/Form101MetaData.model';
import { ExternalIncomeItem } from '@/views/dashboard/form101/steps/tax-details-step/external-income-table/models/external-income-item.model';
import { Vue } from 'vue-property-decorator';

const addToDocuments = (state, doc: Document): Documents => {
  if (doc.docName) {
    doc.name = doc.docName;
  }
  switch ('' + doc.docType) {
    case '201':
      return {
        ...state.documents,
        employeeFiles: [...state.documents.employeeFiles, doc],
      };
    case '300':
      return {
        ...state.documents,
        childrenFiles: [...state.documents.childrenFiles, doc],
      };
    case '500':
      return {
        ...state.documents,
        extraIncomeFiles: [...state.documents.extraIncomeFiles, doc],
      };
    case '802':
      return {
        ...state.documents,
        handicapFiles: [...state.documents.handicapFiles, doc],
      };
    case '8022':
      return {
        ...state.documents,
        handicapMonthlyAllowanceFiles: [...state.documents.handicapMonthlyAllowanceFiles, doc],
      };
    case '803':
      return {
        ...state.documents,
        settlementFiles: [...state.documents.settlementFiles, doc],
      };
    case '804':
      return {
        ...state.documents,
        immigrantFiles: [...state.documents.immigrantFiles, doc],
      };
    case '805':
      return {
        ...state.documents,
        unemployedPartnerFiles: [...state.documents.unemployedPartnerFiles, doc],
      };
    case '810':
      return {
        ...state.documents,
        nonCustodyFiles: [...state.documents.nonCustodyFiles, doc],
      };
    case '811':
      return {
        ...state.documents,
        childSupportFiles: [...state.documents.childSupportFiles, doc],
      };
    case '851':
      return {
        ...state.documents,
        incompetentChildFiles: [...state.documents.incompetentChildFiles, doc],
      };
    case '813':
      return {
        ...state.documents,
        soldierFiles: [...state.documents.soldierFiles, doc],
      };
    case '814':
      return {
        ...state.documents,
        academicFiles: [...state.documents.academicFiles, doc],
      };
    case '901':
      return {
        ...state.documents,
        taxCoordsNoIncomeFiles: [...state.documents.taxCoordsNoIncomeFiles, doc],
      };
    case '902':
      return {
        ...state.documents,
        taxCoordsExternalIncomeFiles: [...state.documents.taxCoordsExternalIncomeFiles, doc],
      };
    case '903':
      return {
        ...state.documents,
        taxCoordsApprovedFiles: [...state.documents.taxCoordsApprovedFiles, doc],
      };
    case '816':
      return {
        ...state.documents,
        reserveFiles: [...state.documents.reserveFiles, doc],
      };
    default:
      return state.documents;
  }
};

interface EmployeeChildDetails {
  value: string;
  index: number;
  property: string;
}

export const mutations: MutationTree<FormState> = {

  [SET_FORM_HEADERS]: (state, formHeaders: FormHeaders) => {
    state.formHeaders = formHeaders;
  },

  [SET_FORM101_DATA]: (state, form101Data: Form101DataModel) => {
    state.form101Data = form101Data;
    state.form101Data = { ...state.form101Data, ...form101Data };
    state.employerDetails = {
      employerAddress: form101Data.employerAddress,
      employerName: form101Data.employerName,
      employerPhone: form101Data.employerPhone,
      employerDeductionNumber: form101Data.employerDeductionNumber,
    };
  },

  [SET_FORM101_META_DATA]: (state, form101MetaData: Form101MetaData) => {
    state.form101MetaData = { ...state.form101MetaData, ...form101MetaData };
  },

  [SET_FORM101_ID]: (state, formId: number) => {
    state.formId = formId;
  },

  // set the create form errors string array
  [SET_CREATE_FORM_ERRORS]: (state, createFormErrors: string[]) => {
    state.createFormErrors = createFormErrors;
  },

  [SET_EDIT_FORM_ERRORS]: (state, editFormErrors: string[]) => {
    state.editFormErrors = editFormErrors;
  },

  [SET_SUBMIT_FORM_ERRORS]: (state, submitFormErrors: string[]) => {
    state.submitFormErrors = submitFormErrors;
  },

  [RESET_FORM_ERRORS]: (state) => {
    state.formErrors = null;
  },

  // set form errors string array
  [SET_FORM_ERRORS]: (state, formErrors: WizardFormError[]) => {
    if (formErrors && formErrors.length) {
      const groupBy = key => array =>
        array.reduce((objectsByKeyValue, obj) => {
          const value = obj[key];
          objectsByKeyValue[value] = (objectsByKeyValue[value] || []).concat(obj);
          return objectsByKeyValue;
        }, {});
      const groupBySectionNumber = groupBy('sectionNumber');
      const errorsGroupBySectionNumber = groupBySectionNumber(formErrors);
      state.formErrors = { ...errorsGroupBySectionNumber };
    } else {
      state.formErrors = null;
    }
  },

  // set the cities array for the select field of cities
  [SET_CITIES]: (state, cities: City[]) => {
    state.cities = cities;
  },

  // set the cities array for the select field of cities
  [SET_SETTLEMENTS]: (state, settlements: City[]) => {
    state.settlements = settlements;
  },

  [SET_FORM_LOADING_MODE]: (state, formLoadingMode: boolean) => {
    state.formLoadingMode = formLoadingMode;
  },

  [SET_FORM_READ_ONLY_MODE]: (state, formReadOnlyMode: boolean) => {
    state.formReadOnlyMode = formReadOnlyMode;
  },

  [SET_OPEN_CONFIRM_FORM_DIALOG]: (state, openConfirmFormDialog: boolean) => {
    state.openConfirmFormDialog = openConfirmFormDialog;
  },

  [SET_FORM_SUCCESS_DIALOG]: (state, formSuccessDialog: boolean) => {
    state.formSuccessDialog = formSuccessDialog;
  },

  [SET_FORM_REJECTED_REASON]: (state, formRejectedReason: string) => {
    state.formRejectedReason = formRejectedReason;
  },

  [SET_NO_PDF_ERROR]: (state, noPdfError: boolean) => {
    state.noPdfError = noPdfError;
  },

  [SET_NO_PDF_ERRORS]: (state, noPdfErrors: string[]) => {
    state.noPdfErrors = noPdfErrors;
  },

  [ADD_EMPLOYEE_CHILD]: (state, child: ChildrenDetails) => {
    const updatedChildren = [...state.form101Data.children];
    updatedChildren.push(child);

    Vue.set(state.form101Data, 'children', updatedChildren);
  },

  [REMOVE_EMPLOYEE_CHILD]: (state, index: number) => {
    const updatedChildren = [...state.form101Data.children];
    updatedChildren.splice(index, 1);

    Vue.set(state.form101Data, 'children', updatedChildren);
  },

  [UPDATE_EMPLOYEE_CHILD_DETAILS]: (state, { value, index, property }: EmployeeChildDetails) => {
    const updatedChild = { ...state.form101Data.children[index] };
    updatedChild[property] = value;

    const updatedChildren = [...state.form101Data.children];
    updatedChildren[index] = updatedChild;

    Vue.set(state.form101Data, 'children', updatedChildren);
  },

  [FORM_RESET_STATE]: (state) => {
    state = Object.assign(state, initialState());
  },

  [SET_TOOLTIP_ID]: (state, value: string) => {
    state.tooltipId = value;
  },

  [SET_FORM_ERRORS_OPEN]: (state, value: boolean) => {
    state.formErrorsOpen = value;
  },

  [SET_FORM_ERRORS_SHOW]: (state, value: boolean) => {
    state.formErrorsShow = value;
  },

  [SET_OPEN_SAVE_BEFORE_LEAVE_WIZARD_MODAL]: (state, openSaveBeforeLeaveWizardModal: boolean) => {
    state.openSaveBeforeLeaveWizardModal = openSaveBeforeLeaveWizardModal;
  },

  [SET_SAVE_BEFORE_LEAVE_WIZARD_ROUTE_NAME]: (state, saveBeforeLeaveWizardRouteName: string) => {
    state.saveBeforeLeaveWizardRouteName = saveBeforeLeaveWizardRouteName;
  },

  [SET_SKIP_SAVE_BEFORE_LEAVE_WIZARD_MODAL]: (state, skipSaveBeforeLeaveWizardModal: boolean) => {
    state.skipSaveBeforeLeaveWizardModal = skipSaveBeforeLeaveWizardModal;
  },

  [SET_DELETE_FORM_ERRORS]: (state, deleteFormErrors: string[]) => {
    state.deleteFormErrors = deleteFormErrors;
  },

  [SET_OPEN_APPROVE_FORM_DIALOG]: (state, openApproveFormDialog: boolean) => {
    state.openApproveFormDialog = openApproveFormDialog;
  },

  [SET_OPEN_REJECT_FORM_DIALOG]: (state, openRejectFormDialog: boolean) => {
    state.openRejectFormDialog = openRejectFormDialog;
  },

  [SET_OPEN_FORM_STATUS_HISTORY_DIALOG]: (state, openFormStatusHistoryDialog: boolean) => {
    state.openFormStatusHistoryDialog = openFormStatusHistoryDialog;
  },

  [SET_OPEN_FORM_101_CHANGES_DIALOG]: (state, openForm101ChangesDialog: boolean) => {
    state.openForm101ChangesDialog = openForm101ChangesDialog;
  },

  [SET_OPEN_CHOOSE_FORM_OFFICE_MODAL]: (state, openChooseFormOfficeModal: boolean) => {
    state.openChooseFormOfficeModal = openChooseFormOfficeModal;
  },

  [SET_CREATE_FORM_LOADING]: (state, createFormLoading: boolean) => {
    state.createFormLoading = createFormLoading;
  },

  [ADD_EXTERNAL_INCOME]: (state, income: ExternalIncomeItem) => {
    const updatedExternalItems = [...state.form101Data?.externalIncomeDetails];

    if (income) {
      updatedExternalItems.push(income);
    }

    Vue.set(state.form101Data, 'externalIncomeDetails', updatedExternalItems);
  },

  [REMOVE_EXTERNAL_INCOME]: (state, externalIncomeItemId: string) => {
    const updatedExternalIncomes = [...state.form101Data?.externalIncomeDetails]
      ?.filter(item => item.uniqueId !== externalIncomeItemId);

    Vue.set(state.form101Data, 'externalIncomeDetails', updatedExternalIncomes);
  },

  [UPDATE_EXTERNAL_INCOME]: (state, { value, property, id }) => {
    const updatedExternalIncomes = [...state.form101Data?.externalIncomeDetails];

    updatedExternalIncomes?.forEach(item => {
      if (item?.uniqueId === id) {
        item[property] = value;
      }
    });

    Vue.set(state.form101Data, 'externalIncomeDetails', updatedExternalIncomes);
  },

  [SET_OPEN_DELETE_FORM_DIALOG]: (state, openDeleteFormModal: boolean) => {
    state.openDeleteFormModal = openDeleteFormModal;
  },

  [SET_APPROVE_DETAILS_CHANGES]: (state, approveDetailsChanges: boolean) => {
    state.approveDetailsChanges = approveDetailsChanges;
  },

  [SET_OPEN_CHANGES_NOTIFICATION_MODAL]: (state, openChangesNotificationModal: boolean) => {
    state.openChangesNotificationModal = openChangesNotificationModal;
  },
};
